import axios, { type AxiosError } from 'axios';
import {
  axiosApiServices,
  axiosAppServices,
  axiosHistoryServices,
  axiosHistoryServicesWithStatus,
  axiosMessagesServices,
  axiosNewApiServices,
  axiosNewAppServices,
  axiosPriorityServices,
  axiosPriorityServicesWithStatus,
  axiosServerNodeServices,
} from 'utils/axios';
import type { SupportClickEntry, SupportConsoleLevel, SupportNetworkFailure } from './types';
import { describeElement } from './element-descriptor';

const AXIOS_INSTANCES = [
  axiosAppServices,
  axiosHistoryServices,
  axiosHistoryServicesWithStatus,
  axiosMessagesServices,
  axiosApiServices,
  axiosPriorityServices,
  axiosPriorityServicesWithStatus,
  axiosNewApiServices,
  axiosNewAppServices,
  axiosServerNodeServices,
] as const;

function buildAxiosErrorUrl(err: AxiosError): string {
  const cfg = err.config;
  if (!cfg) return '';
  const base = (cfg.baseURL || '').replace(/\/$/, '');
  const path = cfg.url || '';
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith('/') ? path : path ? `/${path}` : '';
  if (!base) {
    if (typeof window !== 'undefined' && p) return `${window.location.origin}${p}`;
    return p;
  }
  try {
    return new URL(p || '/', base.endsWith('/') ? base : `${base}/`).href;
  } catch {
    return `${base}${p}`;
  }
}

function previewResponseData(data: unknown): string {
  if (data === undefined || data === null) return '';
  if (typeof data === 'string') return data.slice(0, 2000);
  try {
    return JSON.stringify(data).slice(0, 2000);
  } catch {
    return String(data).slice(0, 2000);
  }
}

function serializeConsoleArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

export type DiagnosticInterceptorHandles = {
  destroy: () => void;
};

export function installDiagnosticInterceptors(opts: {
  isActive: () => boolean;
  pushConsole: (level: SupportConsoleLevel, message: string) => void;
  pushNetwork: (entry: SupportNetworkFailure) => void;
  pushClick: (entry: SupportClickEntry) => void;
}): DiagnosticInterceptorHandles {
  const { isActive, pushConsole, pushNetwork, pushClick } = opts;

  const ejectIds: { instance: (typeof AXIOS_INSTANCES)[number]; id: number }[] = [];
  for (const instance of AXIOS_INSTANCES) {
    const id = instance.interceptors.response.use(
      (response) => response,
      (error: unknown) => {
        if (isActive() && axios.isAxiosError(error)) {
          const status = error.response?.status ?? 0;
          if (status < 200 || status >= 300) {
            const method = (error.config?.method || 'get').toUpperCase();
            pushNetwork({
              source: 'axios',
              method,
              url: buildAxiosErrorUrl(error),
              status,
              responsePreview: previewResponseData(error.response?.data),
              at: new Date().toISOString(),
            });
          }
        }
        return Promise.reject(error);
      },
    );
    ejectIds.push({ instance, id });
  }

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await origFetch(input, init);
    if (isActive() && !response.ok) {
      let url = '';
      if (typeof input === 'string') url = input;
      else if (input instanceof URL) url = input.href;
      else url = input.url;
      let text = '';
      try {
        text = (await response.clone().text()).slice(0, 2000);
      } catch {
        text = '';
      }
      pushNetwork({
        source: 'fetch',
        method: (init?.method || 'GET').toUpperCase(),
        url,
        status: response.status,
        responsePreview: text,
        at: new Date().toISOString(),
      });
    }
    return response;
  };

  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    if (isActive()) {
      pushConsole('error', serializeConsoleArgs(args));
    }
    origError(...args);
  };
  console.warn = (...args: unknown[]) => {
    if (isActive()) {
      pushConsole('warn', serializeConsoleArgs(args));
    }
    origWarn(...args);
  };

  const onClickCapture = (ev: MouseEvent) => {
    if (!isActive()) return;
    if (ev.target instanceof Element) {
      if (ev.target.closest('[data-support-diagnostic-ui="1"]')) return;
      const path = describeElement(ev.target);
      pushClick({ path, at: new Date().toISOString() });
    }
  };
  document.addEventListener('click', onClickCapture, true);

  return {
    destroy: () => {
      ejectIds.forEach(({ instance, id }) => {
        instance.interceptors.response.eject(id);
      });
      window.fetch = origFetch as typeof window.fetch;
      console.error = origError;
      console.warn = origWarn;
      document.removeEventListener('click', onClickCapture, true);
    },
  };
}
